import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import {
  CheckCircle,
  XCircle,
  Loader2,
  X,
  AlertTriangle
} from 'lucide-react';
import { api } from '../../services/api';

interface StockUpdateJob {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  total_items: number | null;
  processed_items: number | null;
  updated_items: number | null;
  current_page: number | null;
  progress_percentage: number | null;
  error_message?: string;
}

interface StockUpdateProgressProps {
  jobId: string;
  onComplete?: () => void;
  onClose?: () => void;
}

export function StockUpdateProgress({ jobId, onComplete, onClose }: StockUpdateProgressProps) {
  const [job, setJob] = useState<StockUpdateJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const response = await api.get<StockUpdateJob>(`/utils/stock-update/${jobId}/status`);
      setJob(response);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load stock update status:', err);
      setError(err.message || 'Failed to load status');
    }
  }, [jobId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (job && ['pending', 'in_progress'].includes(job.status)) {
      const interval = setInterval(() => {
        loadStatus();
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(interval);
    } else if (job && job.status === 'completed' && onComplete) {
      onComplete();
    }
  }, [job?.status, loadStatus, onComplete]);

  const handleCancel = async () => {
    try {
      await api.post(`/utils/stock-update/${jobId}/cancel`);
      loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel stock update');
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!job) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span>Loading stock update status...</span>
      </div>
    );
  }

  const getStatusDisplay = () => {
    switch (job.status) {
      case 'pending':
        return {
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 dark:bg-blue-950',
          icon: <Loader2 className="w-5 h-5 animate-spin" />,
          text: 'Pending'
        };
      case 'in_progress':
        return {
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          icon: <Loader2 className="w-5 h-5 animate-spin" />,
          text: 'Updating Stock'
        };
      case 'completed':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: <CheckCircle className="w-5 h-5" />,
          text: 'Completed'
        };
      case 'failed':
        return {
          color: 'text-destructive',
          bgColor: 'bg-destructive/10',
          icon: <XCircle className="w-5 h-5" />,
          text: 'Failed'
        };
      case 'cancelled':
        return {
          color: 'text-orange-600',
          bgColor: 'bg-orange-50 dark:bg-orange-950',
          icon: <X className="w-5 h-5" />,
          text: 'Cancelled'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          icon: null,
          text: 'Unknown'
        };
    }
  };

  const status = getStatusDisplay();
  const progress = job.progress_percentage || 0;

  return (
    <div className="space-y-4">
      <div className={`flex items-center justify-between p-3 rounded-lg ${status.bgColor}`}>
        <div className="flex items-center gap-2">
          {status.icon}
          <span className={`font-medium ${status.color}`}>{status.text}</span>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {['pending', 'in_progress'].includes(job.status) && (
        <>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span className="font-medium">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Processed</div>
              <div className="font-medium">{job.processed_items?.toLocaleString() || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Updated</div>
              <div className="font-medium">{job.updated_items?.toLocaleString() || 0}</div>
            </div>
          </div>

          {job.total_items && (
            <div className="text-sm">
              <div className="text-muted-foreground">Total Items</div>
              <div className="font-medium">{job.total_items.toLocaleString()}</div>
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="w-full"
          >
            Cancel Stock Update
          </Button>
        </>
      )}

      {job.status === 'completed' && (
        <Alert>
          <CheckCircle className="h-4 w-4 text-success" />
          <AlertDescription>
            Stock update completed successfully!{' '}
            {job.updated_items && `${job.updated_items.toLocaleString()} products updated.`}
          </AlertDescription>
        </Alert>
      )}

      {job.status === 'failed' && job.error_message && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{job.error_message}</AlertDescription>
        </Alert>
      )}

      {job.status === 'cancelled' && (
        <Alert>
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription>Stock update was cancelled.</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
